// genreUtils.js

export const SUPER_GENRE_COLOR_MAP = {
    'Drama': '#2F3E7C',
    'Fantasy': '#7B3FE4',
    'Action': '#D81B37',
    'Thriller': '#0F172A',
    'Romance': '#E83E8C',
    'Science Fiction': '#0EA5E9',
    'Crime': '#0F766E',
    'Comedy': '#F59E0B',
    'Horror': '#7F1D1D',
    'War': '#6B7280',
    'Western': '#8B5E34',
};

// --- Legend Helper Logic ---
export function prepareLegendData(taxonomy, colorMap) {
    const legendData = [];
    if (!taxonomy?.children) return legendData;

    const presentSuperGenres = {};
    const borrowedParents = [];
    const borrowedChildrenMap = {};

    const nodesToScan = [...taxonomy.children];
    while (nodesToScan.length > 0) {
        const currentNode = nodesToScan.shift();
        if (currentNode.type === 'Super-genre') {
            presentSuperGenres[currentNode.name] = currentNode.percentage || 0;
        }
        if (currentNode.borrowed_from) {
            if (!borrowedParents.includes(currentNode.borrowed_from)) {
                borrowedParents.push(currentNode.borrowed_from);
            }
            if (!borrowedChildrenMap[currentNode.borrowed_from]) {
                borrowedChildrenMap[currentNode.borrowed_from] = [];
            }
            borrowedChildrenMap[currentNode.borrowed_from].push(currentNode.name);
        }
        if (currentNode.children) {
            nodesToScan.push(...currentNode.children);
        }
    }

    const allReferencedGenres = [...new Set([...Object.keys(presentSuperGenres), ...borrowedParents])];

    allReferencedGenres.forEach((genreName) => {
        if (colorMap[genreName]) {
            const isNative = Object.prototype.hasOwnProperty.call(presentSuperGenres, genreName);
            const percentage = presentSuperGenres[genreName] || 0;
            const isBorrowedContext = borrowedParents.includes(genreName) && !isNative;

            legendData.push({
                name: genreName,
                color: colorMap[genreName],
                percentage: percentage,
                isBorrowedContext: isBorrowedContext,
                borrowedChildren: borrowedChildrenMap[genreName] || []
            });
        }
    });

    return legendData;
}

// --- Plotly Data Transformation Logic ---
function wrapTextWithHtmlBreaks(text, maxLength = 60) {
    if (!text) return 'No explanation.';
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    words.forEach(word => {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        if (testLine.length <= maxLength) {
            currentLine = testLine;
        } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
        }
    });
    if (currentLine) lines.push(currentLine);
    return lines.join('<br>');
}

function getTextColorForBackground(hexColor) {
    if (!hexColor || hexColor.length < 7) return '#000000';
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance > 128 ? '#000000' : '#FFFFFF';
}

function adjustColorOpacity(hexColor, type) {
    if (!hexColor || hexColor.length < 7) return hexColor;
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    let opacity;
    switch (type) {
        case 'Super-genre': opacity = 1.0; break;
        case 'Macro-genre': opacity = 0.9; break;
        case 'Micro-genre': opacity = 0.8; break;
        default: opacity = 1.0;
    }
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function flattenDataForPlotly(node, parentId = '', parentColor = '#adb5bd', parentName = '', superGenreColorMap) {
    let results = {
        ids: [], labels: [], parents: [], values: [], explanations: [],
        percentages: [], coverages: [], colorMap: {}, fontWeights: [],
        fontSizes: [], cleanLabels: [], fontColors: [], types: [], paths: []
    };

    const nodeId = parentId ? `${parentId}-${node.name}` : node.name;
    const currentPath = parentName ? `${parentName} → ${node.name}` : node.name;

    let currentColor;
    if (node.borrowed_from) {
        currentColor = superGenreColorMap[node.borrowed_from] || '#adb5bd';
    } else if (superGenreColorMap[node.name]) {
        currentColor = superGenreColorMap[node.name];
    } else {
        currentColor = parentColor;
    }

    const adjustedColor = adjustColorOpacity(currentColor, node.type);
    results.colorMap[nodeId] = adjustedColor;

    if (node.strength === 0 && (!node.coverage || node.coverage === 0)) {
        results.colorMap[nodeId] = 'rgba(233, 236, 239, 0.6)';
    }

    const isSuperGenre = node.type === 'Super-genre';
    const fontWeight = isSuperGenre ? 'bold' : 'normal';
    const fontSize = isSuperGenre ? 15 : (node.type === 'Macro-genre' ? 13 : 12);
    const fontColor = getTextColorForBackground(currentColor);

    let onChartLabel = node.name;
    if (node.borrowed_from) {
        onChartLabel += `<br><i style='font-size:0.75em;opacity:0.8'>(from ${node.borrowed_from})</i>`;
    }

    let nodeValue;
    if (node.children && node.children.length > 0) {
        let childrenValueSum = 0;
        let childrenRawCoverageSum = 0;

        node.children.forEach(child => {
            childrenRawCoverageSum += (child.coverage || child.strength);
            const childResults = flattenDataForPlotly(child, nodeId, currentColor, currentPath, superGenreColorMap);
            childrenValueSum += childResults.values[0];

            Object.keys(results).forEach(key => {
                if (key === 'colorMap') {
                    Object.assign(results[key], childResults[key]);
                } else {
                    results[key] = results[key].concat(childResults[key]);
                }
            });
        });

        const parentCoverage = node.coverage || node.strength;
        if (childrenRawCoverageSum < parentCoverage) {
            const unusedValue = parentCoverage - childrenRawCoverageSum;
            const unusedId = `${nodeId}-unused`;

            results.ids.push(unusedId);
            results.labels.push('');
            results.cleanLabels.push('Other');
            results.parents.push(nodeId);
            results.values.push(unusedValue);
            results.percentages.push(0);
            results.coverages.push(0);
            results.explanations.push('Unused capacity - other elements not specified');
            results.fontWeights.push('normal');
            results.fontSizes.push(8);
            results.fontColors.push('#666666');
            results.types.push('unused');
            results.paths.push(currentPath);
            results.colorMap[unusedId] = 'rgba(200, 200, 200, 0.3)';

            childrenValueSum += unusedValue;
        }
        nodeValue = childrenValueSum;
    } else {
        nodeValue = node.coverage || node.strength;
    }

    results.ids.unshift(nodeId);
    results.labels.unshift(onChartLabel);
    results.cleanLabels.unshift(node.name);
    results.parents.unshift(parentId);
    results.values.unshift(nodeValue);
    results.percentages.unshift(node.percentage || 0);
    results.coverages.unshift(node.coverage || 0);
    results.explanations.unshift(wrapTextWithHtmlBreaks(node.explanation, 60));
    results.fontWeights.unshift(fontWeight);
    results.fontSizes.unshift(fontSize);
    results.fontColors.unshift(fontColor);
    results.types.unshift(node.type);
    results.paths.unshift(currentPath);

    return results;
}
